import { useFeatures } from '../Shingles/features';
import { connectLocation } from '../../location/LocationConnectors';
import { PrefixWithLocale } from '../../Utilities';
import { dataTrackAttribute } from '../../helpers/analytics';
import { stringsFor } from '../../location/locales';
import Picture from '../../OC/oc-picture';
import { If } from '../../Utilities';
import Text from '../../OC/oc-text';
import RichText from '../../OC/oc-rich-text';
import _ from 'lodash'

const FeaturePicture = connectLocation(({ locale, feature = {}, alt = "" }) => {
  const { key, image: defaultImage } = feature
  const translations = stringsFor(locale?.code)
  // some of the images have translatable text in them. If we find an image_url in the feature's translations, use it.
  const image = _.get(translations, `shingles.features.${key}.image_url`, defaultImage)
  return (
  <div className="img shingle-pdp-image">
    <Picture source={image} alt={alt} />
  </div>
  )
})

const LearnMore = ({feature, children, locale}) => {
  if (!feature.url) {
    return null;
  }

  return (
    <a
      href={PrefixWithLocale(feature.url, locale.code)}
      className="oc-cta-button transparent"
      data-track="learn-more"
      data-track-element-location="main section CTA"
      data-track-destination={dataTrackAttribute(feature.url, locale.code)}
    >
      {children}
    </a>
  );
};


const FeatureForModal = connectLocation(({feature, locale, t}) => {

  return (
    <>
      <h3 className="modal-heading"><Text content={feature.heading} /></h3>

      <div className="features-content">
        <div className="modal-img">
          <FeaturePicture feature={feature} />
        </div>

        <div className="modal-text">
          <If condition={!!feature.subHeading}>
            <p className="modal-subheading"><Text content={feature.subHeading} /></p>
          </If>

          <RichText content={feature.description} />

          <If condition={false}>
            <a
              href={PrefixWithLocale(feature.url, locale)}
              className="oc-cta-button transparent"
              data-track="learn-more"
              data-track-element-location="main section CTA"
              data-track-destination={dataTrackAttribute(feature.url || '')}
              style={{ marginTop: 20 }}
            >
              <Text content={t('shingles.featuresAndBenefits.cta')} />
            </a>
          </If>
        </div>
      </div>
    </>
  )
})

const Feature = ({ feature, reverse, t, locale }) => (
  <div id={`feature-${feature.key}`} className={`oc-content-and-image ratio-2-1 no-border${reverse ? ' reverse' : ''}`}>
    <div className="content">
      <h3 className="feature-icon">
        <img src={feature.icon} alt={feature.alt}/>
        <div><Text content={feature.heading.replace("†‡", '<span class="reg">†‡</span>')} /></div>
      </h3>

      <p><Text content={feature.description} /></p>

      <LearnMore feature={feature} locale={locale}>
      <Text content={t('shingles.featuresAndBenefits.cta')} />
        <span className="sr-only"><Text content={`${t('shingles.featuresAndBenefits.cta_sr')} ${feature.analyticsOption}`} /></span>
      </LearnMore>
    </div>

    <FeaturePicture feature={feature} />
  </div>
);

const Warranty = ({ warranty, reverse, t, locale }) => (
  <div className={`oc-content-and-image ratio-3-1 no-border${reverse ? ' reverse' : ''}`}>
    <div className="content">
      <h3 className="feature-icon">
        <img src={warranty.icon} alt={warranty.alt}/>
        <Text content={warranty.heading} />
      </h3>

      <p><Text content={warranty.description} /></p>

      <LearnMore feature={warranty} locale={locale}>{t('shingles.featuresAndBenefits.cta')}</LearnMore>
    </div>

    <FeaturePicture feature={warranty} />
  </div>
)

const WindResistance = ({ windResistance, reverse, t, locale }) => (
  <div className={`oc-content-and-image ratio-2-1 no-border${reverse ? ' reverse' : ''}`}>
    <div className="content">
      <h3 className="feature-icon">
        <img src={windResistance.icon} alt={windResistance.alt}/>
        <Text content={windResistance.heading} />
      </h3>

      <p><Text content={windResistance.description} /></p>

      <LearnMore feature={windResistance} locale={locale}>{t('shingles.featuresAndBenefits.cta')}</LearnMore>
    </div>

    <FeaturePicture feature={windResistance} />
  </div>
)

const Features = connectLocation(({ shingle, t, locale }) => {
  const features = useFeatures();
  if (!features) {
    return null;
  }
  const availableFeatures = features.filter(feature => feature.isInShingleOrColor(shingle))

  const renderFeature = (shingle, feature, reverse) => {
    switch (feature.category) {
      case 'feature':
        return (feature.isInShingleOrColor(shingle) ? (
          <Feature key={feature.key} t={t} locale={locale} feature={feature} reverse={reverse} />
        ) : '')
      case 'warranty':
        return (feature.name === shingle.warranty ? (
          <Warranty key={feature.key} t={t} locale={locale} warranty={feature} reverse={reverse} />
        ) : '')
      case 'wind_resistance':
        return (feature.value === shingle.wind_resistance ? (
          <WindResistance key={feature.key} t={t} locale={locale} windResistance={feature} reverse={reverse} />
        ) : '')
      case 'impact_resistance':
        return null;
      default:
        return feature.category
    }
  }

  return (
    <section id="features" className="pdp-features">
      <h2 className="heading-with-icons section-heading">
        {t('shingles.featuresAndBenefits.title')}
        <span className="icons">
          {availableFeatures.map(feature => <img key={feature.key} src={feature.icon} title={feature.name} alt={feature.alt} />)}
        </span>
      </h2>
      {availableFeatures.map((feature, i) => renderFeature(shingle, feature, !(i % 2)))}
    </section>
  )
});

export { Features, FeatureForModal };
